import { isAxiosError } from 'axios'
import type { JobInfoResponse } from '~/@types/backend-types'
import { JobInfoStatus } from '~/state/jobInfo/reducer'

import { getRequestJobsInfoByJobUuid } from '@capture/client-api/src/orval'

import { buildCommonParams, buildRequestOptions, getForeignAuthParam } from './api'
import { queryAccountInfoWithCache } from './app.queries'
import { getPasswordForJob } from './index'

export class QueryJobInfoError extends Error {
    status: JobInfoStatus

    constructor(status: JobInfoStatus, cause?: unknown) {
        super('Failed to query job info')
        this.status = status
        this.cause = cause instanceof Error ? cause : undefined
    }
}

/**
 * Check if user has a valid authenticated session by attempting to fetch account info.
 * queryAccountInfoWithCache handles the hasStoredAuthCredentials check internally.
 */
async function isLoggedIn(): Promise<boolean> {
    try {
        await queryAccountInfoWithCache()
        return true
    } catch {
        return false
    }
}

/**
 * Query job info.
 * Does NOT dispatch Redux actions - pure data fetching.
 * Does NOT use cache - always fetches fresh data.
 * Throws QueryJobInfoError on failure with appropriate JobInfoStatus.
 *
 * @param jobID - The job ID to fetch info for
 */
export async function queryJobInfo(jobID: JobID): Promise<JobInfoResponse> {
    const password = getPasswordForJob(jobID)
    const commonParams = buildCommonParams(getForeignAuthParam(password))

    try {
        const response = await getRequestJobsInfoByJobUuid(
            jobID,
            { ...commonParams, include_details: true },
            buildRequestOptions({ hostType: 'appHost', password }),
        )
        return response.data as JobInfoResponse
    } catch (err) {
        if (!isAxiosError(err)) {
            throw new QueryJobInfoError(JobInfoStatus.FETCH_FAILED, err)
        }

        const responseStatus = err.response?.status
        const data = err.response?.data

        // Backend returns HTTP400 for password-protected stories with auth="", and sometimes 403
        if (responseStatus === 400 || responseStatus === 401 || responseStatus === 403) {
            if (typeof data === 'string' && data.includes('public job auth failed')) {
                throw new QueryJobInfoError(JobInfoStatus.PASSWORD_REQUIRED, err)
            }
            if (
                typeof data === 'string' &&
                data.includes('User does not have access to secured job')
            ) {
                // If user is logged in but doesn't have access, treat as NOT_FOUND
                // If user is not logged in, they might have access after logging in
                if (await isLoggedIn()) {
                    throw new QueryJobInfoError(JobInfoStatus.NOT_FOUND, err)
                }
                throw new QueryJobInfoError(JobInfoStatus.LOGIN_REQUIRED, err)
            }
            throw new QueryJobInfoError(JobInfoStatus.NOT_FOUND, err)
        }

        if (responseStatus === 404) {
            throw new QueryJobInfoError(JobInfoStatus.NOT_FOUND, err)
        }

        throw new QueryJobInfoError(JobInfoStatus.FETCH_FAILED, err)
    }
}
